import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  PlusIcon, 
  TrashIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { api, workoutsAPI, setTypesAPI } from '../../services/api';

// Workout creation schema
const workoutSchema = z.object({
  name: z.string().min(1, 'Workout name is required').max(100),
  scheduledDate: z.string().min(1, 'Workout date is required'),
  estimatedDuration: z.number().min(1).optional(),
  description: z.string().max(500).optional(),
  tags: z.array(z.string()).optional()
});

type WorkoutFormData = z.infer<typeof workoutSchema>;

interface Exercise {
  _id: string;
  name: string;
  description: string;
  musclesWorked: string[];
  equipment?: string[];
  instructions?: string[];
  category?: string;
  videoUrl?: string;
}

interface ExerciseSet {
  setNumber: number;
  targetReps: number;
  targetWeight?: string;
  targetDuration?: number;
  targetDistance?: number;
  weightUnit?: 'lbs' | 'kg';
  eachSide?: boolean;
}

interface WorkoutExercise {
  exerciseId: string;
  exerciseName: string;
  order: number;
  equipment?: string[];
  duration?: string;
  tempo?: string;
  description?: string;
  specialInstructions?: string;
  sets: ExerciseSet[];
}

interface WorkoutSet {
  setNumber: number;
  setType?: string;
  restPeriod?: string;
  specialInstructions?: string;
  intensityLevel?: number;
  targetHeartRate?: number;
  description?: string;
  exercises: WorkoutExercise[];
}

// Helper function to check if exercise has duration/tempo info
const hasDurationOrTempo = (exercise: WorkoutExercise) => {
  return (exercise.duration && exercise.duration.trim()) || 
         (exercise.tempo && exercise.tempo.trim());
};

const hasValidSets = (exercise: WorkoutExercise) => {
  return exercise.sets && exercise.sets.length > 0 && 
         exercise.sets.some(set => set.targetReps > 0);
};

// In-memory cache for exercises
let exerciseCache: Exercise[] | null = null;
let exerciseCacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function WorkoutBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  const workoutId = searchParams.get('workoutId');

  const { user } = useAuthStore();

  // Form management
  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<WorkoutFormData>({
    resolver: zodResolver(workoutSchema),
    defaultValues: {
      name: '',
      scheduledDate: '',
      estimatedDuration: undefined,
      description: '',
      tags: []
    }
  });

  // State
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [workoutSets, setWorkoutSets] = useState<WorkoutSet[]>([
    {
      setNumber: 1,
      setType: '',
      exercises: []
    }
  ]);
  const [currentSetIndex, setCurrentSetIndex] = useState<number | null>(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exerciseError, setExerciseError] = useState<string | null>(null);
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [setTypes, setSetTypes] = useState<Array<{ name: string; sortOrder: number }>>([]);
  const [isLoadingSetTypes, setIsLoadingSetTypes] = useState(false);
  const [expandedSets, setExpandedSets] = useState<Set<number>>(new Set([0])); // First set expanded by default
  const [expandedExercises, setExpandedExercises] = useState<Map<number, Set<number>>>(new Map()); // setIndex -> Set<exerciseIndex>
  const [workoutDetailsExpanded, setWorkoutDetailsExpanded] = useState(true); // Workout details expanded by default
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingWorkout, setIsLoadingWorkout] = useState(false);
  const [clients, setClients] = useState<Array<{ id: string; firstName: string; lastName: string }>>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copyWorkoutName, setCopyWorkoutName] = useState('');
  const [copySelectedClientId, setCopySelectedClientId] = useState('');
  const [copyScheduledDate, setCopyScheduledDate] = useState('');
  const [isCopyingWorkout, setIsCopyingWorkout] = useState(false);
  const [originalWorkoutData, setOriginalWorkoutData] = useState<any>(null);

  // Fetch exercises with caching
  const fetchExercises = useCallback(async () => {
    // Check cache first
    if (exerciseCache && exerciseCacheTimestamp && 
        (Date.now() - exerciseCacheTimestamp < CACHE_DURATION)) {
      setExercises(exerciseCache);
      setFilteredExercises(exerciseCache);
      return;
    }

    try {
      setIsLoadingExercises(true);
      const response = await api.get('/libraries/exercises');
      const exerciseList = response.data.exercises || response.data;
      
      // Update cache
      exerciseCache = exerciseList;
      exerciseCacheTimestamp = Date.now();
      
      setExercises(exerciseList);
      setFilteredExercises(exerciseList);
      setExerciseError(null);
    } catch (error: any) {
      console.error('Error fetching exercises:', error);
      setExerciseError(error.response?.data?.error || error.message || 'Failed to fetch exercises');
    } finally {
      setIsLoadingExercises(false);
    }
  }, []);

  // Filter exercises based on search
  useEffect(() => {
    if (!exerciseSearch.trim()) {
      setFilteredExercises(exercises);
      return;
    }

    const searchLower = exerciseSearch.toLowerCase();
    const filtered = exercises.filter(exercise =>
      exercise.name.toLowerCase().includes(searchLower) ||
      exercise.description.toLowerCase().includes(searchLower) ||
      (exercise.musclesWorked || []).some(mg => mg.toLowerCase().includes(searchLower)) ||
      (exercise.category || '').toLowerCase().includes(searchLower)
    );
    setFilteredExercises(filtered);
  }, [exerciseSearch, exercises]);

  // Load exercises on mount
  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  // Fetch set types
  const fetchSetTypes = useCallback(async () => {
    try {
      setIsLoadingSetTypes(true);
      const response = await setTypesAPI.getAll();
      setSetTypes(response.data.setTypes || []);
    } catch (error: any) {
      console.error('Error fetching set types:', error);
    } finally {
      setIsLoadingSetTypes(false);
    }
  }, []);

  // Load set types on mount
  useEffect(() => {
    fetchSetTypes();
  }, [fetchSetTypes]);

  // Fetch trainer's clients
  const fetchClients = useCallback(async () => {
    try {
      setIsLoadingClients(true);
      const response = await api.get('/clients');
      setClients(response.data.clients || response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  }, []);

  // Load clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Fetch existing workout data if editing
  const fetchWorkoutData = useCallback(async () => {
    if (!workoutId) return;

    try {
      setIsLoadingWorkout(true);
      const response = await workoutsAPI.getById(workoutId);
      const workout = response.data;

      // Populate form with existing data
      reset({
        name: workout.name,
        scheduledDate: workout.scheduledDate ? new Date(workout.scheduledDate).toISOString().split('T')[0] : '',
        estimatedDuration: workout.estimatedDuration,
        description: workout.description || '',
        tags: workout.tags || []
      });

      // Populate workout sets
      setWorkoutSets(workout.sets || [{
        setNumber: 1,
        setType: '',
        exercises: []
      }]);

      // Collapse all sets and exercises when editing
      setExpandedSets(new Set());
      setExpandedExercises(new Map());
      setWorkoutDetailsExpanded(false);

      // Store original workout data for copying
      setOriginalWorkoutData(workout);
      setIsEditing(true);
    } catch (error: any) {
      console.error('Error fetching workout:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch workout');
    } finally {
      setIsLoadingWorkout(false);
    }
  }, [workoutId, reset]);



  // Load workout data if editing
  useEffect(() => {
    if (workoutId) {
      fetchWorkoutData();
    }
  }, [workoutId, fetchWorkoutData]);

  // Accordion management functions
  const toggleSetExpanded = (setIndex: number) => {
    const newExpandedSets = new Set(expandedSets);
    if (newExpandedSets.has(setIndex)) {
      newExpandedSets.delete(setIndex);
    } else {
      newExpandedSets.add(setIndex);
    }
    setExpandedSets(newExpandedSets);
  };

  const toggleExerciseExpanded = (setIndex: number, exerciseIndex: number) => {
    const newExpandedExercises = new Map(expandedExercises);
    const setExercises = new Set(newExpandedExercises.get(setIndex) || []);
    
    if (setExercises.has(exerciseIndex)) {
      setExercises.delete(exerciseIndex);
    } else {
      setExercises.add(exerciseIndex);
    }
    
    newExpandedExercises.set(setIndex, setExercises);
    setExpandedExercises(newExpandedExercises);
  };

  const collapseAllSets = () => {
    setExpandedSets(new Set());
  };

  const collapseAllExercises = (setIndex: number) => {
    const newExpandedExercises = new Map(expandedExercises);
    newExpandedExercises.set(setIndex, new Set());
    setExpandedExercises(newExpandedExercises);
  };

  const toggleWorkoutDetailsExpanded = () => {
    setWorkoutDetailsExpanded(!workoutDetailsExpanded);
  };

  // Check authorization and client ID
  useEffect(() => {
    if (!user?.roles?.includes('TRAINER')) {
      navigate('/dashboard');
      return;
    }

    if (!clientId) {
      navigate('/trainer/clients');
      return;
    }
  }, [user, clientId, navigate]);

  // Add new set
  const addSet = () => {
    const newSet: WorkoutSet = {
      setNumber: workoutSets.length + 1,
      setType: '',
      exercises: []
    };
    setWorkoutSets([...workoutSets, newSet]);
    // Collapse all sets and expand the new one
    collapseAllSets();
    setExpandedSets(new Set([workoutSets.length]));
  };

  // Remove set
  const removeSet = (setIndex: number) => {
    if (workoutSets.length <= 1) return;
    
    const updatedSets = workoutSets.filter((_, index) => index !== setIndex);
    // Renumber sets
    const renumberedSets = updatedSets.map((set, index) => ({
      ...set,
      setNumber: index + 1
    }));
    setWorkoutSets(renumberedSets);
  };

  // Move set up/down
  const moveSet = (setIndex: number, direction: 'up' | 'down') => {
    const newSets = [...workoutSets];
    const targetIndex = direction === 'up' ? setIndex - 1 : setIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newSets.length) return;
    
    [newSets[setIndex], newSets[targetIndex]] = [newSets[targetIndex], newSets[setIndex]];
    
    // Renumber sets
    const renumberedSets = newSets.map((set, index) => ({
      ...set,
      setNumber: index + 1
    }));
    setWorkoutSets(renumberedSets);
  };

  // Add exercise to set
  const addExerciseToSet = (setIndex: number, exercise: Exercise) => {
    const updatedSets = [...workoutSets];
    const existingExerciseIndex = updatedSets[setIndex].exercises.findIndex(
      ex => ex.exerciseId === exercise._id
    );

    if (existingExerciseIndex >= 0) {
      // Exercise already exists, add another set
      const existingExercise = updatedSets[setIndex].exercises[existingExerciseIndex];
      existingExercise.sets.push({
        setNumber: existingExercise.sets.length + 1,
        targetReps: 0
      });
    } else {
             // Add new exercise
       const newExercise: WorkoutExercise = {
         exerciseId: exercise._id,
         exerciseName: exercise.name,
         order: updatedSets[setIndex].exercises.length + 1,
         equipment: exercise.equipment || [],
         description: (exercise.description || '').substring(0, 500), // Truncate to 500 chars
         // Start with one empty set (can be removed if using duration/tempo only)
         sets: [{
           setNumber: 1,
           targetReps: 0
         }]
       };
      updatedSets[setIndex].exercises.push(newExercise);
    }

    setWorkoutSets(updatedSets);
    setShowExerciseSelector(false);
    setCurrentSetIndex(null);
    setExerciseSearch(''); // Clear search after selection
    
    // Collapse all exercises in this set and expand the new one
    if (existingExerciseIndex >= 0) {
      // Exercise already exists, just add another set
      collapseAllExercises(setIndex);
      const newExpandedExercises = new Map(expandedExercises);
      const setExercises = new Set([existingExerciseIndex]);
      newExpandedExercises.set(setIndex, setExercises);
      setExpandedExercises(newExpandedExercises);
    } else {
      // Add new exercise
      collapseAllExercises(setIndex);
      const newExpandedExercises = new Map(expandedExercises);
      const setExercises = new Set([updatedSets[setIndex].exercises.length - 1]);
      newExpandedExercises.set(setIndex, setExercises);
      setExpandedExercises(newExpandedExercises);
    }
  };

     // Change exercise in set
   const changeExerciseInSet = (setIndex: number, exerciseIndex: number, exercise: Exercise) => {
     const updatedSets = [...workoutSets];
     updatedSets[setIndex].exercises[exerciseIndex] = {
       ...updatedSets[setIndex].exercises[exerciseIndex],
       exerciseId: exercise._id,
       exerciseName: exercise.name,
       equipment: exercise.equipment || [],
       description: (exercise.description || '').substring(0, 500) // Truncate to 500 chars
     };
     setWorkoutSets(updatedSets);
     setShowExerciseSelector(false);
     setCurrentSetIndex(null);
     setExerciseSearch(''); // Clear search after selection
   };

           // Clone reps and weight from one set to the next set within the same exercise
    const cloneSetRepsWeight = (setIndex: number, exerciseIndex: number, sourceSetIdx: number) => {
      const updatedSets = [...workoutSets];
      const exercise = updatedSets[setIndex].exercises[exerciseIndex];
      const sourceSet = exercise.sets[sourceSetIdx];
      
      // Create a new set with the same values
      const newSet: ExerciseSet = {
        setNumber: exercise.sets.length + 1,
        targetReps: sourceSet.targetReps,
        targetWeight: sourceSet.targetWeight,
        weightUnit: sourceSet.weightUnit,
        eachSide: sourceSet.eachSide
      };
      
      // Add the new set to the exercise
      exercise.sets.push(newSet);
      setWorkoutSets(updatedSets);
    };

  // Remove exercise from set
  const removeExerciseFromSet = (setIndex: number, exerciseIndex: number) => {
    const updatedSets = [...workoutSets];
    updatedSets[setIndex].exercises.splice(exerciseIndex, 1);
    
    // Reorder remaining exercises
    updatedSets[setIndex].exercises.forEach((exercise, index) => {
      exercise.order = index + 1;
    });
    
    setWorkoutSets(updatedSets);
  };

  // Handle copy workout
  const handleCopyWorkout = async () => {
    if (!copyWorkoutName.trim() || !copySelectedClientId || !copyScheduledDate) {
      setError('Please fill in all required fields for copying');
      return;
    }

    setIsCopyingWorkout(true);
    setError(null);
    
    try {
      const workoutData = {
        name: copyWorkoutName,
        scheduledDate: copyScheduledDate,
        estimatedDuration: originalWorkoutData?.estimatedDuration,
        description: originalWorkoutData?.description,
        assignedTo: copySelectedClientId,
        sets: workoutSets
      };

      const response = await workoutsAPI.create(workoutData);
      const newId = response.data?.id || response.data?._id;
      setShowCopyModal(false);

      if (!newId) {
        setError('Workout copied but no ID was returned');
        return;
      }

      navigate(
        `/trainer/workout-builder?clientId=${copySelectedClientId}&workoutId=${newId}`
      );
    } catch (error: any) {
      console.error('Error copying workout:', error);
      setError(error.response?.data?.error || error.message || 'Failed to copy workout');
    } finally {
      setIsCopyingWorkout(false);
    }
  };

  // Open copy modal
  const openCopyModal = () => {
    // Get current workout name from the form
    const currentWorkoutName = watch('name') || '';
    setCopyWorkoutName(currentWorkoutName ? `Copy of ${currentWorkoutName}` : 'Copy of Workout');
    
    // Default to current client
    setCopySelectedClientId(clientId || '');
    setCopyScheduledDate('');
    setShowCopyModal(true);
  };

  // Submit workout
  const onSubmit = async (data: WorkoutFormData) => {
    if (!clientId || workoutSets.length === 0) return;

    // Validate that all exercises have valid sets with reps
    for (const set of workoutSets) {
      for (const exercise of set.exercises) {
        const hasValidExerciseSets = hasValidSets(exercise);
        const hasDurationTempo = hasDurationOrTempo(exercise);
        
        // Exercise must have either valid sets OR duration/tempo
        if (!hasValidExerciseSets && !hasDurationTempo) {
          setError(`Exercise "${exercise.exerciseName}" must have either sets with reps OR duration/tempo specified`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const workoutData = {
        ...data,
        assignedTo: clientId,
        sets: workoutSets
      };

      if (isEditing && workoutId) {
        await workoutsAPI.update(workoutId, workoutData);
        setShowSuccessModal(true);
      } else {
        const response = await workoutsAPI.create(workoutData);
        const newId = response.data?.id || response.data?._id;
        if (!newId) {
          setError('Workout created but no ID was returned');
          return;
        }
        navigate(`/trainer/workout-builder?clientId=${clientId}&workoutId=${newId}`);
      }
    } catch (error: any) {
      console.error('Error saving workout:', error);
      setError(error.response?.data?.error || error.message || `Failed to ${isEditing ? 'update' : 'create'} workout`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user?.roles?.includes('TRAINER') || !clientId) {
    return null;
  }

  if (isLoadingWorkout) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Workout' : 'Create Workout'}
          </h1>
          <div className="flex items-center space-x-2">
            {/* Copy button - only show when editing an existing workout */}
            {isEditing && workoutId && (
              <button
                onClick={openCopyModal}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                <span>Copy</span>
              </button>
            )}
            <button
              onClick={() => navigate(clientId ? `/trainer/clients/${clientId}` : '/trainer/clients')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Exercise Selector Modal */}
      {showExerciseSelector && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full max-w-lg max-h-[80vh] flex flex-col rounded-t-lg sm:rounded-lg">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 rounded-t-lg">
                             <div className="flex items-center justify-between">
                 <div>
                   <h3 className="text-lg font-semibold">
                     {currentExerciseIndex !== null ? 'Change Exercise' : 'Select Exercise'}
                   </h3>
                 </div>
                                 <button
                   onClick={() => {
                     setShowExerciseSelector(false);
                     setCurrentExerciseIndex(null);
                   }}
                   className="text-gray-600 hover:text-gray-900"
                 >
                   ✕
                 </button>
              </div>
              {/* Search */}
              <div className="mt-3 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={exerciseSearch}
                  onChange={(e) => setExerciseSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Exercise List */}
            <div className="flex-1 overflow-y-auto px-4 py-2">
              {isLoadingExercises ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <div className="text-gray-500 text-sm mt-2">Loading exercises...</div>
                </div>
              ) : exerciseError ? (
                <div className="text-center py-8">
                  <div className="text-red-600 text-sm">{exerciseError}</div>
                  <button
                    onClick={fetchExercises}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Retry
                  </button>
                </div>
              ) : filteredExercises.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {exerciseSearch ? 'No exercises match your search' : 'No exercises available'}
                </div>
              ) : (
                                 filteredExercises.map((exercise) => (
                   <button
                     key={exercise._id}
                     onClick={() => {
                       if (currentSetIndex !== null) {
                         if (currentExerciseIndex !== null) {
                           // Changing existing exercise
                           changeExerciseInSet(currentSetIndex, currentExerciseIndex, exercise);
                         } else {
                           // Adding new exercise
                           addExerciseToSet(currentSetIndex, exercise);
                         }
                       }
                     }}
                     className="w-full text-left p-3 rounded-lg hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                   >
                     <div className="font-medium text-gray-900">{exercise.name}</div>
                     {(exercise.equipment || []).length > 0 && (
                       <div className="text-xs text-gray-500 mt-1">
                         Equipment: {(exercise.equipment || []).join(', ')}
                       </div>
                     )}
                   </button>
                 ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Workout Updated</h3>
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">Your changes have been saved.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Workout Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Copy Workout</h3>
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workout Name *
                  </label>
                  <input
                    type="text"
                    value={copyWorkoutName}
                    onChange={(e) => setCopyWorkoutName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to Client *
                  </label>
                  <select
                    value={copySelectedClientId}
                    onChange={(e) => setCopySelectedClientId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isLoadingClients}
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.firstName} {client.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheduled Date *
                  </label>
                  <input
                    type="date"
                    value={copyScheduledDate}
                    onChange={(e) => setCopyScheduledDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => setShowCopyModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCopyWorkout}
                disabled={isCopyingWorkout || !copyWorkoutName.trim() || !copySelectedClientId || !copyScheduledDate}
                className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isCopyingWorkout ? 'Copying...' : 'Copy Workout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="py-4 space-y-4">
        {/* Workout Details Form */}
        <div>
          <div className="bg-white rounded-lg shadow-sm">
            {/* Workout Details Header - Clickable for accordion */}
            <div 
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
              onClick={toggleWorkoutDetailsExpanded}
            >
              <div className="flex items-center space-x-2">
                <ChevronDownIcon 
                  className={`h-5 w-5 text-gray-500 transition-transform ${
                    workoutDetailsExpanded ? 'rotate-180' : ''
                  }`} 
                />
                <h3 className="text-base font-semibold">Workout Details</h3>
              </div>
            </div>

            {/* Workout Details Content - Collapsible */}
            {workoutDetailsExpanded && (
              <div className="px-3 pb-3">
                <div className="space-y-3">


                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Workout Name *
                    </label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      )}
                    />
                    {errors.name && (
                      <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Workout Date *
                    </label>
                    <Controller
                      name="scheduledDate"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                          onClick={(e) => e.currentTarget.showPicker()}
                        />
                      )}
                    />
                    {errors.scheduledDate && (
                      <p className="text-red-600 text-sm mt-1">{errors.scheduledDate.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Duration (minutes)
                    </label>
                    <Controller
                      name="estimatedDuration"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <input
                          {...field}
                          type="number"
                          value={value || ''}
                          onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          min="1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workout Sets */}
        <div>
          <div className="space-y-3">
            {workoutSets.map((set, setIndex) => (
              <div key={setIndex} className="bg-white rounded-lg shadow-sm">
                {/* Set Header - Clickable for accordion */}
                <div 
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleSetExpanded(setIndex)}
                >
                  <div className="flex items-center space-x-2">
                    <ChevronDownIcon 
                      className={`h-5 w-5 text-gray-500 transition-transform ${
                        expandedSets.has(setIndex) ? 'rotate-180' : ''
                      }`} 
                    />
                    <h3 className="text-base font-semibold">
                      Set {set.setNumber} - {set.setType || 'No Type'}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    {setIndex > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSet(setIndex, 'up');
                        }}
                        className="p-1 text-gray-600 hover:text-gray-900"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                    )}
                    {setIndex < workoutSets.length - 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSet(setIndex, 'down');
                        }}
                        className="p-1 text-gray-600 hover:text-gray-900"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                    )}
                    {workoutSets.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSet(setIndex);
                        }}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Set Content - Collapsible */}
                {expandedSets.has(setIndex) && (
                  <div className="px-3 pb-3">
                    {/* Set Configuration */}
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Set Type
                        </label>
                        <select
                          value={set.setType || ''}
                          onChange={(e) => {
                            const updatedSets = [...workoutSets];
                            updatedSets[setIndex].setType = e.target.value;
                            setWorkoutSets(updatedSets);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={isLoadingSetTypes}
                        >
                          <option value=""></option>
                          {setTypes.map((setType) => (
                            <option key={setType.name} value={setType.name}>
                              {setType.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rest Period
                        </label>
                        <input
                          type="text"
                          value={set.restPeriod || ''}
                          onChange={(e) => {
                            const updatedSets = [...workoutSets];
                            updatedSets[setIndex].restPeriod = e.target.value;
                            setWorkoutSets(updatedSets);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Special Instructions
                        </label>
                        <input
                          type="text"
                          value={set.specialInstructions || ''}
                          onChange={(e) => {
                            const updatedSets = [...workoutSets];
                            updatedSets[setIndex].specialInstructions = e.target.value;
                            setWorkoutSets(updatedSets);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Intensity Level / RPE (1-10)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={set.intensityLevel || ''}
                          onChange={(e) => {
                            const updatedSets = [...workoutSets];
                            updatedSets[setIndex].intensityLevel = e.target.value ? parseInt(e.target.value) : undefined;
                            setWorkoutSets(updatedSets);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Target Heart Rate (BPM)
                        </label>
                        <input
                          type="number"
                          min="60"
                          max="220"
                          value={set.targetHeartRate || ''}
                          onChange={(e) => {
                            const updatedSets = [...workoutSets];
                            updatedSets[setIndex].targetHeartRate = e.target.value ? parseInt(e.target.value) : undefined;
                            setWorkoutSets(updatedSets);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Exercises in this set */}
                    <div className="space-y-3">
                      {set.exercises.map((exercise, exerciseIndex) => (
                        <div key={exerciseIndex} className="border border-gray-200 rounded-lg">
                                                     {/* Exercise Header - Clickable for accordion */}
                           <div 
                             className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                             onClick={() => toggleExerciseExpanded(setIndex, exerciseIndex)}
                           >
                             <div className="flex items-center space-x-2">
                               <ChevronDownIcon 
                                 className={`h-4 w-4 text-gray-500 transition-transform ${
                                   expandedExercises.get(setIndex)?.has(exerciseIndex) ? 'rotate-180' : ''
                                 }`} 
                               />
                               <h4 className="font-medium text-gray-900">{exercise.exerciseName}</h4>
                             </div>
                             <div className="flex items-center space-x-2">
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   setCurrentSetIndex(setIndex);
                                   setShowExerciseSelector(true);
                                   // Store the exercise index to change
                                   setCurrentExerciseIndex(exerciseIndex);
                                 }}
                                 className="text-blue-600 hover:text-blue-800"
                                 title="Change Exercise"
                               >
                                 <MagnifyingGlassIcon className="h-4 w-4" />
                               </button>
                               
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   removeExerciseFromSet(setIndex, exerciseIndex);
                                 }}
                                 className="text-red-600 hover:text-red-800"
                               >
                                 <TrashIcon className="h-4 w-4" />
                               </button>
                             </div>
                           </div>

                          {/* Exercise Content - Collapsible */}
                          {expandedExercises.get(setIndex)?.has(exerciseIndex) && (
                            <div className="px-3 pb-3">
                              {/* Exercise Sets - Always available */}
                              <div className="space-y-2 mb-3">
                                <label className="block text-xs font-medium text-gray-700">
                                  Sets & Reps (Optional if Duration/Tempo specified)
                                </label>
                                {exercise.sets.map((exerciseSet, setIdx) => (
                                  <div key={setIdx} className="space-y-2">
                                    {/* Labels for first set only */}
                                    {setIdx === 0 && (
                                      <div className="flex items-center space-x-2">
                                        <div className="w-12 flex justify-center">
                                          <span className="text-xs text-gray-500">Set</span>
                                        </div>
                                        <div className="w-16 flex justify-center">
                                          <span className="text-xs text-gray-500">Reps</span>
                                        </div>
                                        <div className="w-16 flex justify-center">
                                          <span className="text-xs text-gray-500">Weight</span>
                                        </div>
                                                                                 <div className="w-24"></div> {/* Space for unit buttons */}
                                         <div className="w-16"></div> {/* Space for clone and delete buttons */}
                                      </div>
                                    )}
                                    
                                    {/* Set row with aligned inputs */}
                                    <div className="flex items-end space-x-2">
                                      {/* Set number */}
                                      <div className="w-12 flex justify-center">
                                        <span className="text-sm font-medium text-gray-600">{exerciseSet.setNumber}</span>
                                      </div>
                                      
                                      {/* Reps input */}
                                      <div className="w-16">
                                        <input
                                          type="number"
                                          min="1"
                                          value={exerciseSet.targetReps || ''}
                                          onChange={(e) => {
                                            const updatedSets = [...workoutSets];
                                            updatedSets[setIndex].exercises[exerciseIndex].sets[setIdx].targetReps = parseInt(e.target.value) || 0;
                                            setWorkoutSets(updatedSets);
                                          }}
                                          className={`w-full px-2 py-1 border rounded text-sm text-center ${
                                            exerciseSet.targetReps <= 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                          }`}
                                        />
                                      </div>
                                      
                                                                             {/* Weight input */}
                                       <div className="w-16">
                                         <input
                                           type="text"
                                           value={exerciseSet.targetWeight || ''}
                                           onChange={(e) => {
                                             const updatedSets = [...workoutSets];
                                             updatedSets[setIndex].exercises[exerciseIndex].sets[setIdx].targetWeight = e.target.value;
                                             setWorkoutSets(updatedSets);
                                           }}
                                           className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                         />
                                       </div>
                                     
                                      {/* Unit buttons */}
                                      <div className="flex space-x-1">
                                        <button
                                          type="button"
                                          aria-label="Set weight unit to pounds"
                                          onClick={() => {
                                            const updatedSets = [...workoutSets];
                                            updatedSets[setIndex].exercises[exerciseIndex].sets[setIdx].weightUnit = 'lbs';
                                            setWorkoutSets(updatedSets);
                                          }}
                                          className={`px-2 py-1 text-xs border rounded ${
                                            exerciseSet.weightUnit === 'lbs' 
                                              ? 'bg-blue-500 text-white border-blue-500' 
                                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                          }`}
                                        >
                                          lbs
                                        </button>
                                        <button
                                          type="button"
                                          aria-label="Set weight unit to kilograms"
                                          onClick={() => {
                                            const updatedSets = [...workoutSets];
                                            updatedSets[setIndex].exercises[exerciseIndex].sets[setIdx].weightUnit = 'kg';
                                            setWorkoutSets(updatedSets);
                                          }}
                                          className={`px-2 py-1 text-xs border rounded ${
                                            exerciseSet.weightUnit === 'kg' 
                                              ? 'bg-blue-500 text-white border-blue-500' 
                                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                          }`}
                                        >
                                          kg
                                        </button>
                                        <button
                                          type="button"
                                          aria-label="Toggle each side weight"
                                          onClick={() => {
                                            const updatedSets = [...workoutSets];
                                            updatedSets[setIndex].exercises[exerciseIndex].sets[setIdx].eachSide = !exerciseSet.eachSide;
                                            setWorkoutSets(updatedSets);
                                          }}
                                          className={`px-2 py-1 text-xs border rounded ${
                                            exerciseSet.eachSide 
                                              ? 'bg-green-500 text-white border-green-500' 
                                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                          }`}
                                          title={exerciseSet.eachSide ? 'Weight per side' : 'Total weight'}
                                        >
                                          ea
                                        </button>
                                      </div>
                                      
                                                                             {/* Clone and Delete buttons */}
                                       <div className="flex space-x-1">
                                         {/* Clone button - always show and always enabled */}
                                         <button
                                           onClick={() => cloneSetRepsWeight(setIndex, exerciseIndex, setIdx)}
                                           className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                                           title="Copy reps and weight to new set"
                                         >
                                           <DocumentDuplicateIcon className="h-4 w-4" />
                                         </button>
                                         {/* Delete button - only show when there are multiple sets */}
                                         {exercise.sets.length > 1 && (
                                           <button
                                             onClick={() => {
                                               const updatedSets = [...workoutSets];
                                               updatedSets[setIndex].exercises[exerciseIndex].sets.splice(setIdx, 1);
                                               // Renumber sets
                                               updatedSets[setIndex].exercises[exerciseIndex].sets.forEach((s, idx) => {
                                                 s.setNumber = idx + 1;
                                               });
                                               setWorkoutSets(updatedSets);
                                             }}
                                             className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                           >
                                             <TrashIcon className="h-4 w-4" />
                                           </button>
                                         )}
                                       </div>
                                    </div>
                                  </div>
                                ))}
                               
                                <button
                                  onClick={() => {
                                    const updatedSets = [...workoutSets];
                                    const newSet: ExerciseSet = {
                                      setNumber: exercise.sets.length + 1,
                                      targetReps: 0
                                    };
                                    updatedSets[setIndex].exercises[exerciseIndex].sets.push(newSet);
                                    setWorkoutSets(updatedSets);
                                  }}
                                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  <PlusIcon className="h-3 w-3" />
                                  <span>Add Set</span>
                                </button>
                              </div>

                              {/* Exercise Configuration - Always available */}
                              <div className="space-y-2 mb-3 text-sm">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Duration (Optional - use instead of or in addition to sets)
                                  </label>
                                  <input
                                    type="text"
                                    value={exercise.duration || ''}
                                    onChange={(e) => {
                                      const updatedSets = [...workoutSets];
                                      updatedSets[setIndex].exercises[exerciseIndex].duration = e.target.value;
                                      setWorkoutSets(updatedSets);
                                    }}
                                    className="w-full px-2 py-1 border border-gray-300 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Tempo/Intensity (Optional)
                                  </label>
                                  <input
                                    type="text"
                                    value={exercise.tempo || ''}
                                    onChange={(e) => {
                                      const updatedSets = [...workoutSets];
                                      updatedSets[setIndex].exercises[exerciseIndex].tempo = e.target.value;
                                      setWorkoutSets(updatedSets);
                                    }}
                                    className="w-full px-2 py-1 border border-gray-300 rounded"
                                  />
                                </div>
                              </div>

                              {/* Description / Tip */}
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Description / Tip
                                </label>
                                <input
                                  type="text"
                                  value={exercise.description || ''}
                                  onChange={(e) => {
                                    const updatedSets = [...workoutSets];
                                    updatedSets[setIndex].exercises[exerciseIndex].description = e.target.value;
                                    setWorkoutSets(updatedSets);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>

                              {/* Special Instructions */}
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Special Instructions
                                </label>
                                <input
                                  type="text"
                                  value={exercise.specialInstructions || ''}
                                  onChange={(e) => {
                                    const updatedSets = [...workoutSets];
                                    updatedSets[setIndex].exercises[exerciseIndex].specialInstructions = e.target.value;
                                    setWorkoutSets(updatedSets);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Add Exercise Button */}
                      <button
                        onClick={() => {
                          setCurrentSetIndex(setIndex);
                          setShowExerciseSelector(true);
                        }}
                        className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600"
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Add Exercise</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add Set Button */}
            <button
              onClick={addSet}
              className="w-full flex items-center justify-center space-x-2 py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Set</span>
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error creating workout</h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-4">
          {workoutSets.every(set => set.exercises.length === 0) && (
            <div className="text-center text-gray-500 text-sm mb-3">
              Add at least one exercise to create a workout
            </div>
          )}
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || workoutSets.every(set => set.exercises.length === 0)}
            className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <CheckIcon className="h-5 w-5" />
            )}
            <span>
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Workout' : 'Create Workout')
              }
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
